import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  YardInspection,
  UpdateYardInspectionParams,
} from "../../../store/oasis/yardInspectionsStore";
import {
  deriveInspectionStatus,
  manualExitMissing,
  inspectionStatusClasses,
  inspectionStatusIcon,
  inspectionStatusLabel,
} from "../../../store/oasis/yardInspectionsStore";
import {
  PROVIDER_VISIT_FIELDS,
  providerVisitDirty,
  providerVisitFormFrom,
  providerVisitPayload,
  validateProviderVisit,
  type ProviderVisitError,
  type ProviderVisitField,
  type ProviderVisitForm,
} from "../../../store/oasis/specialInspection";
import SpecialTagBadge from "../../../components/inspections/SpecialTagBadge";
import { brand } from "../../../config/theme";
import { formatYardTsMinutes, modalYardName } from "../inspectionTimes";

const MAX_LENGTH = Object.fromEntries(
  PROVIDER_VISIT_FIELDS.map((f) => [f.field, f.maxLength]),
) as Record<ProviderVisitField, number>;

/**
 * Inspección especial de PROVEEDOR: la visita que entró con el tag de
 * proveedores del patio.
 *
 * Es un modal aparte y no una variante del de descarga porque no comparte
 * nada con él: acá no hay fotos, ni parámetros de agua, ni vehículo que
 * identificar, ni salida que ligar. Sólo tres datos —quién vino, quién lo
 * recibió, a qué— y la visita en sólo lectura. Esconder con condicionales lo
 * que sobra del otro modal dejaría montados sus efectos (el fetch de geocerca,
 * el de candidatos de salida) para una visita que no los usa.
 */
export default function ProviderVisitModal({
  inspection,
  themeClasses,
  isDarkMode,
  yardTimeZone,
  onClose,
  onSave,
  onManualExit,
}: {
  inspection: YardInspection;
  themeClasses: any;
  isDarkMode: boolean;
  yardTimeZone?: string;
  onClose: () => void;
  onSave: (data: UpdateYardInspectionParams) => Promise<void>;
  // "DAR SALIDA MANUAL" (POST /yardInspection/manualExit + pluma de salida).
  // Proveedores no traen tag: se manda sin preguntas de tag. Opcional: sin él
  // la sección no aparece.
  onManualExit?: (
    inspection: YardInspection,
    survey: { tag_presence?: string | null; tag_position?: string | null },
  ) => Promise<boolean>;
}) {
  const { t } = useTranslation("truckYard");
  const status = deriveInspectionStatus(inspection);
  const surface = isDarkMode ? "bg-[#252525]" : "bg-gray-50";
  const labelColor = isDarkMode ? "text-gray-400" : "text-gray-500";
  const inputCls = `w-full px-3 py-2.5 ${surface} border border-transparent rounded-xl ${themeClasses.text} focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 outline-none transition-all text-sm`;

  const [form, setForm] = useState<ProviderVisitForm>(() =>
    providerVisitFormFrom(inspection),
  );
  const [errors, setErrors] = useState<
    Partial<Record<ProviderVisitField, ProviderVisitError>>
  >({});
  const [saving, setSaving] = useState(false);

  // El historial monta el modal con `{selectedInspection && …}`, pero el padre
  // puede cambiar de inspección sin desmontar: sin esto el formulario de la
  // visita nueva heredaría lo escrito en la anterior.
  useEffect(() => {
    setForm(providerVisitFormFrom(inspection));
    setErrors({});
    // Sólo al cambiar de inspección: después de guardar, el padre devuelve la
    // misma inspección actualizada y pisar el formulario ahí borraría lo que
    // el usuario siguiera escribiendo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspection.id]);

  const dirty = providerVisitDirty(form, inspection);

  // Salida manual: sólo con sus tres datos GUARDADOS (manualExitMissing, la
  // misma regla que el backend). Sin preguntas de tag: proveedores no traen.
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [exitBusy, setExitBusy] = useState(false);
  // Lo que FALTA y "hay cambios sin guardar" van separados: con los tres
  // datos escritos pero sin guardar, no falta nada — falta guardar.
  const exitMissing = manualExitMissing(inspection);
  const exitBlocked = dirty || exitMissing.length > 0;
  useEffect(() => {
    if (exitBlocked) setExitConfirmOpen(false);
  }, [exitBlocked]);
  useEffect(() => setExitConfirmOpen(false), [inspection.id]);
  const confirmManualExit = async () => {
    if (!onManualExit || exitBlocked) return;
    setExitBusy(true);
    try {
      if (await onManualExit(inspection, {})) setExitConfirmOpen(false);
    } finally {
      setExitBusy(false);
    }
  };

  const setField = (field: ProviderVisitField, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const found = validateProviderVisit(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSaving(true);
    try {
      await onSave({ id: inspection.id, ...providerVisitPayload(form) });
    } finally {
      setSaving(false);
    }
  };

  const errorText = (field: ProviderVisitField): string | null => {
    const code = errors[field];
    if (code === "required") return t("specialInspection.provider.errors.required");
    if (code === "tooLong") {
      return t("specialInspection.provider.errors.tooLong", {
        max: MAX_LENGTH[field],
      });
    }
    return null;
  };

  const yardName = modalYardName(inspection.yard_name);
  const fieldLabel: Record<ProviderVisitField, string> = {
    provider_name: t("specialInspection.provider.providerName"),
    received_by: t("specialInspection.provider.receivedBy"),
    visit_reason: t("specialInspection.provider.visitReason"),
  };
  const fieldPlaceholder: Record<ProviderVisitField, string> = {
    provider_name: t("specialInspection.provider.providerNamePlaceholder"),
    received_by: t("specialInspection.provider.receivedByPlaceholder"),
    visit_reason: t("specialInspection.provider.visitReasonPlaceholder"),
  };

  const visitFacts: { label: string; value: string; mono?: boolean }[] = [
    {
      label: t("specialInspection.visit.folio"),
      value: inspection.folio != null ? String(inspection.folio) : "—",
    },
    {
      label: t("specialInspection.visit.yard"),
      value: yardName || "—",
    },
    {
      label: t("specialInspection.visit.entry"),
      value: inspection.entry_timestamp
        ? formatYardTsMinutes(inspection.entry_timestamp, yardTimeZone)
        : t("specialInspection.visit.noEntry"),
    },
    {
      label: t("specialInspection.visit.exit"),
      // Con el evento de salida ligado pero sin hora legible se dice "—" y no
      // "sin salida": la salida está, lo que falta es su hora.
      value: inspection.access_exit_id
        ? formatYardTsMinutes(inspection.exit_timestamp, yardTimeZone)
        : t("specialInspection.visit.noExit"),
    },
    {
      label: t("specialInspection.visit.tag"),
      value: inspection.card_number || "—",
      mono: true,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative ${themeClasses.cardBg} rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto`}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-6 py-5 border-b border-gray-700/30 bg-inherit rounded-t-2xl">
          <div className="flex items-center gap-3 min-w-0 flex-wrap">
            <h2 className={`min-w-0 text-base font-bold ${themeClasses.text}`}>
              <span className="block">
                {t("specialInspection.provider.title")}
              </span>
              {yardName && (
                <span
                  className={`block truncate text-xs font-medium ${themeClasses.textSecondary}`}
                >
                  <span className="sr-only"> · </span>
                  <i aria-hidden="true" className="ri-building-4-line mr-1"></i>
                  {yardName}
                </span>
              )}
            </h2>
            <span
              className={`text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1 flex-shrink-0 whitespace-nowrap ${inspectionStatusClasses(status)}`}
            >
              <i className={`${inspectionStatusIcon(status)} text-xs`}></i>
              {inspectionStatusLabel(status)}
            </span>
            <SpecialTagBadge kind="provider" className="flex-shrink-0" />
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("specialInspection.close")}
            className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors flex-shrink-0`}
          >
            <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="p-6 space-y-5">
            <div
              className={`rounded-xl p-3 flex items-start gap-2.5 ${
                isDarkMode
                  ? "bg-purple-500/10 border border-purple-500/25"
                  : "bg-purple-50 border border-purple-200"
              }`}
            >
              <i
                className={`ri-information-line text-base mt-0.5 ${
                  isDarkMode ? "text-purple-400" : "text-purple-600"
                }`}
              ></i>
              <p
                className={`text-xs leading-relaxed ${
                  isDarkMode ? "text-purple-300" : "text-purple-800"
                }`}
              >
                {t("specialInspection.provider.intro")}
              </p>
            </div>

            {/* La visita, en sólo lectura */}
            <div className={`${surface} rounded-xl p-4`}>
              <p
                className={`text-[11px] uppercase tracking-widest font-semibold ${labelColor} mb-3`}
              >
                <i className="ri-door-lock-line mr-1"></i>
                {t("specialInspection.visit.title")}
              </p>
              <dl className="grid grid-cols-2 gap-4">
                {visitFacts.map((fact) => (
                  <div key={fact.label} className="min-w-0">
                    <dt
                      className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}
                    >
                      {fact.label}
                    </dt>
                    <dd
                      className={`text-sm ${themeClasses.text} truncate ${fact.mono ? "font-mono" : ""}`}
                    >
                      {fact.value}
                    </dd>
                  </div>
                ))}
              </dl>
              {yardTimeZone && (
                <p className={`mt-3 text-[11px] ${themeClasses.textSecondary}`}>
                  <i aria-hidden="true" className="ri-time-zone-line mr-1"></i>
                  {t("specialInspection.visit.timeZone", { zone: yardTimeZone })}
                </p>
              )}
            </div>

            {/* Los tres datos */}
            <div className="space-y-4">
              <p
                className={`text-[11px] uppercase tracking-widest font-semibold ${labelColor}`}
              >
                <i className="ri-edit-line mr-1"></i>
                {t("specialInspection.provider.formTitle")}
              </p>
              {PROVIDER_VISIT_FIELDS.map(({ field, maxLength }) => {
                const id = `provider-visit-${field}`;
                const error = errorText(field);
                const shared = {
                  id,
                  value: form[field],
                  maxLength,
                  placeholder: fieldPlaceholder[field],
                  "aria-invalid": !!error,
                  "aria-describedby": error ? `${id}-error` : undefined,
                  className: `${inputCls} ${error ? "!border-red-500/60" : ""}`,
                };
                return (
                  <div key={field}>
                    <label
                      htmlFor={id}
                      className={`text-xs font-medium ${labelColor} mb-1.5 block`}
                    >
                      {fieldLabel[field]} <span className="text-red-500">*</span>
                    </label>
                    {field === "visit_reason" ? (
                      <textarea
                        {...shared}
                        rows={3}
                        onChange={(e) => setField(field, e.target.value)}
                      />
                    ) : (
                      <input
                        {...shared}
                        type="text"
                        onChange={(e) => setField(field, e.target.value)}
                      />
                    )}
                    {error && (
                      <span
                        id={`${id}-error`}
                        className="text-red-400 text-xs mt-1 block"
                      >
                        {error}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Salida manual: la lectora de salida no lee a un proveedor (no trae
                tag). Cierra la visita y levanta la pluma de salida. */}
            {onManualExit && !inspection.access_exit_id && (
              <div className={`${surface} rounded-xl p-4 space-y-3`}>
                <p className={`text-[11px] uppercase tracking-widest font-semibold ${labelColor}`}>
                  <i className="ri-logout-box-r-line mr-1"></i>Salida
                </p>
                {!exitConfirmOpen ? (
                  <button
                    type="button"
                    onClick={() => setExitConfirmOpen(true)}
                    disabled={exitBlocked}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold tracking-wide transition-all bg-red-500/15 hover:bg-red-500/25 text-red-500 border border-red-500/40 disabled:opacity-60"
                  >
                    <i className="ri-logout-box-r-line text-base"></i>
                    DAR SALIDA MANUAL
                  </button>
                ) : (
                  <div className="space-y-2">
                    <p className={`text-xs ${themeClasses.textSecondary}`}>
                      Se registrará la salida del proveedor, se cerrará la visita y se levantará la pluma de salida.
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setExitConfirmOpen(false)}
                        disabled={exitBusy}
                        className={`flex-1 py-2 rounded-xl text-sm font-medium border ${themeClasses.cardBorder} ${themeClasses.buttonText} ${themeClasses.buttonHover} disabled:opacity-60`}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={confirmManualExit}
                        disabled={exitBusy}
                        className="flex-[2] py-2 rounded-xl text-sm font-bold flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
                      >
                        {exitBusy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-logout-box-r-line"></i>}
                        Confirmar salida y levantar pluma
                      </button>
                    </div>
                  </div>
                )}
                {dirty && (
                  <p className="text-xs text-amber-500">
                    <i className="ri-save-line mr-1"></i>Guarda los cambios antes de dar salida manual.
                  </p>
                )}
                {exitMissing.length > 0 && (
                  <div className="flex items-start gap-2 p-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10">
                    <i className="ri-error-warning-line text-amber-500 mt-0.5"></i>
                    <div className={`text-xs ${themeClasses.textSecondary}`}>
                      <p className="font-medium mb-0.5">Completa los datos del proveedor para dar salida manual. Falta:</p>
                      <ul className="list-disc ml-4">
                        {exitMissing.map((m) => (
                          <li key={m}>{m}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="sticky bottom-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-700/30 bg-inherit rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              className={`px-5 py-2.5 rounded-xl ${themeClasses.buttonHover} ${themeClasses.buttonText} font-medium text-sm transition-all border ${themeClasses.cardBorder}`}
            >
              {t("specialInspection.close")}
            </button>
            <button
              type="submit"
              disabled={saving || !dirty}
              className="px-5 py-2.5 rounded-xl font-medium text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: brand.primary, color: brand.accent }}
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-1"></i>
                  {t("specialInspection.saving")}
                </>
              ) : (
                <>
                  <i className="ri-save-line mr-1"></i>
                  {t("specialInspection.save")}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
